import { Backdrop, Box, Card, Chip, Fade, Grid, Modal, Tab, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { BulkReportRecord, GroupReport } from '../store/types';
import { DateTimeFormat, downloadCustomCsv, getLocalDateTime } from 'src/common';
import { InitiateReportRequest } from 'src/features/clients/client/reportsV2/store/types';
import WO2Button from 'src/common/components/button/Button';
import { getReportingNames } from '../../store/utils';
import { buildReportParametersCsv } from '../store/utils';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import {
  REPORT_STATUS_COLORS,
  REPORT_STATUS_NAMES,
  REPORTING_DETAILS_KEY,
  REPORTING_LOGS_KEY,
  REPORTING_MEMBER_LOGS_KEY,
  REPORTING_MEMBERS_KEY,
} from '../../constants';
import {
  DataGridPro,
  GridColumns,
  GridCsvExportOptions,
  GridRenderCellParams,
  GridToolbarContainer,
  GridValueGetterParams,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { LoadingProgress } from 'src/common/store/types';
import { Download } from '@mui/icons-material';

interface Props {
  record: BulkReportRecord;
  reportParameters?: InitiateReportRequest;
  handleClose: () => void;
  hasClientEditAdminOnly: boolean;
  downloadReport: (record: BulkReportRecord) => void;
  terminateRecord: (currentPage: number, currentPageSize: number, recordIndex: number) => void;
  fetchGroupReport: (groupBatchId: string) => void;
  terminateRecordProgress: LoadingProgress;
  groupReport?: GroupReport;
  currentPage: number;
  currentPageSize: number;
  isOpen: boolean;
}

export const ReportingBatchRecordModal = (props: Props): JSX.Element | null => {
  const { batchId, recordIndex, clientName, clientOrGroupId, afslName, practiceName, createdOn, updatedOn, status, groupBatchId } = props.record;
  const [tab, setTab] = useState<string>(REPORTING_DETAILS_KEY);

  useEffect(() => {
    if (props.isOpen && groupBatchId) {
      props.fetchGroupReport(groupBatchId);
    }
  }, [props.isOpen]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => setTab(newValue);

  const downloadParameters = () => {
    const params = props.reportParameters;
    if (!params) return;
    downloadCustomCsv(buildReportParametersCsv(params), `${props.record.batchId}`);
  };

  const terminateRequest = () => {
    props.terminateRecord(props.currentPage, props.currentPageSize, recordIndex);
    props.handleClose();
  };

  const dataRow = (text: string, value: string | undefined, allowWrap?: boolean) => {
    return (
      <Grid item container spacing={2} alignItems={allowWrap ? 'normal' : 'center'} wrap="nowrap" display={!value ? 'none' : 'flex'}>
        <Grid item xs={4}>
          <Typography fontWeight={700} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          {allowWrap ? (
            <Typography>{value}</Typography>
          ) : (
            <Typography noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {value}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  const logsToolbar = () => {
    const apiRef = useGridApiContext();
    const handleExport = (options?: GridCsvExportOptions) => apiRef.current.exportDataAsCsv(options);

    return (
      <GridToolbarContainer sx={{ padding: 2, alignItems: 'center' }}>
        <Box flex={1} />
        <Box>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <WO2Button onClick={() => handleExport({ fileName: 'Logs' })} startIcon={<Download />}>
                Export
              </WO2Button>
            </Grid>
          </Grid>
        </Box>
      </GridToolbarContainer>
    );
  };

  const ReportingBatchLogsColumns: GridColumns = [
    { field: 'relatedUserId', headerName: 'Related User ID', flex: 1 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{params.row.description}</Typography>
          </Box>
        );
      },
    },
    { field: 'clientId', headerName: 'Client ID', flex: 1 },
    { field: 'clientName', headerName: 'Client Name', flex: 1 },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
      valueFormatter: (params) => getLocalDateTime(params.value, DateTimeFormat.DateTimeShort),
    },
  ];

  const ReportingBatchMembersColumns: GridColumns = [
    { field: 'clientName', headerName: 'Client Name', flex: 1 },
    { field: 'adviserName', headerName: 'Adviser Name', flex: 1 },
    { field: 'afslName', headerName: 'AFSL Name', flex: 1 },
    { field: 'practiceName', headerName: 'Practice Name', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => {
        const value = params.value ?? '0';
        return REPORT_STATUS_NAMES[value] ?? null;
      },
      renderCell: (params: GridRenderCellParams<string>) => {
        const color = REPORT_STATUS_COLORS[params.value ?? 'Not Started'];
        return <Chip label={params.value} color={color} />;
      },
    },
  ];

  const showMembers = props.hasClientEditAdminOnly && props.record.isClientGroup;

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={() => {
        props.handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.isOpen}>
        <Card
          elevation={0}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: 2,
            minWidth: 350,
            maxWidth: tab === REPORTING_DETAILS_KEY ? 580 : 1000,
            width: '100%',
          }}
        >
          <TabContext value={tab}>
            <TabList TabIndicatorProps={{ style: { height: '4px' } }} onChange={handleTabChange}>
              <Tab value={REPORTING_DETAILS_KEY} label="Details" />
              <Tab value={REPORTING_LOGS_KEY} label="Logs" hidden={!props.hasClientEditAdminOnly} />
              {showMembers && <Tab value={REPORTING_MEMBERS_KEY} label="Members" />}
              {showMembers && <Tab value={REPORTING_MEMBER_LOGS_KEY} label="Member Logs" />}
            </TabList>

            {tab === REPORTING_DETAILS_KEY && (
              <TabPanel value={REPORTING_DETAILS_KEY}>
                <Grid container direction="column" spacing={4}>
                  <Grid item container direction="column" spacing={1}>
                    {dataRow('Batch ID', batchId)}
                    {dataRow('Client Name(s)', clientName, true)}
                    {dataRow('AFSL', afslName, true)}
                    {dataRow('Practice', practiceName, true)}
                    {dataRow('Created', getLocalDateTime(createdOn, DateTimeFormat.DateTimeShort))}
                    {dataRow('Updated', getLocalDateTime(updatedOn, DateTimeFormat.DateTimeShort))}
                    {dataRow('Status', REPORT_STATUS_NAMES[status])}
                  </Grid>

                  {props.reportParameters && (
                    <>
                      <Grid item>
                        <Typography fontWeight={700}>Report Parameters</Typography>
                      </Grid>
                      <Grid item container direction="column" spacing={1}>
                        {dataRow('Report(s)', getReportingNames(props.reportParameters))}
                        {dataRow('Client ID(s)', clientOrGroupId.toString())}
                        {dataRow('From Date', getLocalDateTime(props.reportParameters.fromDate, DateTimeFormat.DateTimeShort))}
                        {dataRow('To Date', getLocalDateTime(props.reportParameters.toDate, DateTimeFormat.DateTimeShort))}

                        <Grid item container spacing={2} mt={2} flexWrap="nowrap">
                          <Grid item flex={1}>
                            <WO2Button variant="outlined" onClick={props.handleClose}>
                              Close
                            </WO2Button>
                          </Grid>
                          {props.record.status === 'P' && (
                            <Grid item>
                              <LoadingButton loading={props.terminateRecordProgress.isLoading} variant="contained" onClick={terminateRequest}>
                                Cancel Report
                              </LoadingButton>
                            </Grid>
                          )}
                          {props.record.status === 'P' ||
                            (props.record.status === 'C' && (
                              <Grid item display="none">
                                <WO2Button variant="contained" onClick={downloadParameters}>
                                  Download Parameters
                                </WO2Button>
                              </Grid>
                            ))}
                          {props.record.status === 'C' && (
                            <Grid item>
                              <WO2Button variant="contained" onClick={() => props.downloadReport(props.record)}>
                                Download Report
                              </WO2Button>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </TabPanel>
            )}
            {tab === REPORTING_LOGS_KEY && (
              <TabPanel value={REPORTING_LOGS_KEY} hidden={!props.hasClientEditAdminOnly}>
                <Grid container direction="column">
                  <Grid item>
                    <DataGridPro
                      autoHeight
                      pagination
                      getRowId={(row) => row.id}
                      disableSelectionOnClick
                      checkboxSelection
                      columns={ReportingBatchLogsColumns}
                      rows={props.record.logEntries}
                      components={{
                        Toolbar: logsToolbar,
                      }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            )}
            {tab === REPORTING_MEMBERS_KEY && (
              <TabPanel value={REPORTING_MEMBERS_KEY}>
                <Grid container direction="column">
                  <Grid item>
                    <DataGridPro
                      autoHeight
                      pagination
                      getRowId={(row) => row.recordIndex}
                      disableSelectionOnClick
                      columns={ReportingBatchMembersColumns}
                      rows={props.groupReport?.records ?? []}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            )}
            {tab === REPORTING_MEMBER_LOGS_KEY && (
              <TabPanel value={REPORTING_MEMBER_LOGS_KEY}>
                <Grid container direction="column">
                  <Grid item>
                    <DataGridPro
                      autoHeight
                      pagination
                      checkboxSelection
                      getRowId={(row) => row.id}
                      disableSelectionOnClick
                      columns={ReportingBatchLogsColumns}
                      rows={props.groupReport?.logEntries ?? []}
                      components={{
                        Toolbar: logsToolbar,
                      }}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            )}
          </TabContext>
        </Card>
      </Fade>
    </Modal>
  );
};
